import { useEffect, useState } from "react";
import { Clock, Percent } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { fmtMoney } from "@/lib/format";

// Live countdown to an ISO timestamp. Returns parts + expired flag.
export function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetIso) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [targetIso]);
  const target = targetIso ? new Date(targetIso).getTime() : 0;
  const diff = Math.max(0, target - now);
  const totalSeconds = Math.floor(diff / 1000);
  return {
    expired: !!targetIso && diff <= 0,
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

const pad = (n) => String(n).padStart(2, "0");

function TimeBlock({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <span className="tabular-nums font-display text-2xl font-semibold text-foreground sm:text-3xl">{pad(value)}</span>
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
  );
}

/**
 * Urgency banner shown above pricing. `meta` is the top-level /plans response
 * ({ phase, launch_ends_at, early_bird_until, discount_percent, spots_left }).
 */
export function PricingUrgencyBanner({ meta, className = "" }) {
  const phase = meta?.phase;
  const flashActive = !!meta?.flash_discount_active;
  const promoTargetIso = phase === "promo" ? (meta?.price_increase_at || meta?.launch_ends_at) : null;
  // The flash sale is the more urgent, bigger incentive — show it first when both could apply.
  const targetIso = flashActive ? meta?.flash_discount_ends_at : promoTargetIso;
  const cd = useCountdown(targetIso);

  if (flashActive) {
    const maxPct = meta?.max_flash_discount_percent || 30;
    const showDays = cd.days > 0;
    return (
      <div data-testid="flash-discount-banner"
        className={`rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/40 p-5 sm:p-6 card-shadow ${className}`}>
        <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-between sm:text-left">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground"><Percent className="h-5 w-5" /></span>
            <div>
              <h3 className="font-display text-lg font-semibold text-foreground">Flash sale — save up to {maxPct}%</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">15% off the 1 &amp; 3-month plans, <span className="font-medium text-foreground">30% off the 6-month plan</span>. Your discounted price locks in for every renewal — this offer ends soon.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3" data-testid="flash-discount-timer" aria-label="Time left on the flash sale">
            {showDays && (<><TimeBlock value={cd.days} label="days" /><span className="pb-4 text-2xl text-muted-foreground">:</span></>)}
            <TimeBlock value={cd.hours} label="hrs" />
            <span className="pb-4 text-2xl text-muted-foreground">:</span>
            <TimeBlock value={cd.minutes} label="min" />
            <span className="pb-4 text-2xl text-muted-foreground">:</span>
            <TimeBlock value={cd.seconds} label="sec" />
          </div>
        </div>
      </div>
    );
  }

  if (phase === "promo") {
    const showDays = cd.days > 0;
    return (
      <div data-testid="launch-countdown-banner"
        className={`rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/40 p-5 sm:p-6 card-shadow ${className}`}>
        <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-between sm:text-left">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground"><Clock className="h-5 w-5" /></span>
            <div>
              <h3 className="font-display text-lg font-semibold text-foreground">Prices increase soon</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">Lock in the current rate now — prices rise to <span className="font-medium text-foreground">A$200 / A$329 / A$499</span> when the timer hits zero.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3" data-testid="launch-countdown-timer" aria-label="Time until prices increase">
            {showDays && (<><TimeBlock value={cd.days} label="days" /><span className="pb-4 text-2xl text-muted-foreground">:</span></>)}
            <TimeBlock value={cd.hours} label="hrs" />
            <span className="pb-4 text-2xl text-muted-foreground">:</span>
            <TimeBlock value={cd.minutes} label="min" />
            <span className="pb-4 text-2xl text-muted-foreground">:</span>
            <TimeBlock value={cd.seconds} label="sec" />
          </div>
        </div>
      </div>
    );
  }

  return null;
}

/**
 * The strikethrough / discount line under a plan's price. `plan` is one item from /plans.plans.
 */
export function PlanPriceMeta({ plan, className = "" }) {
  if (!plan) return null;
  if (plan.flash_discount_active && plan.flash_discount_percent > 0) {
    const savings = Math.max(0, (plan.pre_flash_amount || 0) - (plan.amount || 0));
    return (
      <div className={className} data-testid={`plan-meta-${plan.id}`}>
        <div className="mt-1 flex items-center gap-2 text-sm">
          <span className="text-muted-foreground line-through">{fmtMoney(plan.pre_flash_amount)}</span>
          <Badge className="bg-primary text-primary-foreground hover:bg-primary">-{plan.flash_discount_percent}% off</Badge>
        </div>
        {savings > 0 && (
          <p className="mt-1 text-xs font-medium text-primary">You save {fmtMoney(savings)}</p>
        )}
      </div>
    );
  }
  const hasStrike = plan.list_amount > plan.amount;
  if (plan.phase === "promo" && hasStrike) {
    return (
      <div className={`mt-1 flex items-center gap-2 text-sm text-muted-foreground ${className}`} data-testid={`plan-meta-${plan.id}`}>
        <span className="line-through">{fmtMoney(plan.list_amount)}</span>
        <Badge variant="secondary" className="bg-accent text-primary">Limited-time price</Badge>
      </div>
    );
  }
  return null;
}
